import { Injectable } from '@angular/core';
import { Action, select, Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import * as MapLayersActions from './projects.actions';
import * as fromProjects from './projects.reducer';
import * as MapLayersSelectors from './projects.selectors';
import { MapLayer } from './projects.types';

@Injectable({ providedIn: 'root' })
export class ProjectsFacade {
  mapLayers$ = this.store.pipe(select(MapLayersSelectors.selectMapLayers));
  pvgeoId$ = this.store.pipe(select(MapLayersSelectors.selectPvgeoId));
  pvgeoGeomsQty$ = this.store.pipe(
    select(MapLayersSelectors.selectPvgeoGeomsQty)
  );
  ouvCollabId$ = this.store.pipe(select(MapLayersSelectors.selectOuvCollabId));

  icadId$ = this.store.pipe(select(MapLayersSelectors.selectIcadId));

  icadGeomsQty$ = this.store.pipe(
    select(MapLayersSelectors.selectIcadGeomsQty)
  );
  zoneId$ = this.store.pipe(select(MapLayersSelectors.selectZoneId));
  icadAPIAddr$ = this.store.pipe(select(MapLayersSelectors.selectIcadAPIAddr));
  extent$ = this.store.pipe(select(MapLayersSelectors.selectExtent));
  legend$ = this.store.pipe(select(MapLayersSelectors.selectLegend));
  city$ = this.store.pipe(select(MapLayersSelectors.selectCity));
  configuracao$ = this.store.pipe(
    select(MapLayersSelectors.selectConfiguracao)
  );
  mode$ = this.store.pipe(select(MapLayersSelectors.selectMode));
  hasBothPortalModes$ = this.store.pipe(
    select(MapLayersSelectors.selectHasBothPortalModes)
  );
  icadDefault$ = this.store.pipe(select(MapLayersSelectors.selectICADDefault));

  vm2CoreMedian$ = this.store.pipe(
    select(MapLayersSelectors.selectVm2CoreMedian)
  );

  icadMode$ = combineLatest([
    this.icadId$,
    this.ouvCollabId$,
    this.pvgeoId$,
  ]).pipe(
    map(([icadId, ouvCollabId, pvgeoId]) => icadId && !ouvCollabId && !pvgeoId)
  );

  currentPvgeoId$ = this.store.pipe(
    select(MapLayersSelectors.selectCurrentPvgeoId)
  );
  currentOuvCollabId$ = this.store.pipe(
    select(MapLayersSelectors.selectCurrentOuvCollabId)
  );
  currentIcadId$ = this.store.pipe(
    select(MapLayersSelectors.selectCurrentIcadId)
  );
  currentZoneId$ = this.store.pipe(
    select(MapLayersSelectors.selectCurrentZoneId)
  );

  selectMapLayerByName$ = (name: string) =>
    this.store.pipe(select(MapLayersSelectors.selectMapLayerByName(name)));

  selectZIdexByRepresentation$ = (representation: string) =>
    this.store.pipe(
      select(MapLayersSelectors.selectZIdexByRepresentation(representation))
    );

  selectMapLayerByType$ = (type: string) =>
    this.store.pipe(select(MapLayersSelectors.selectMapLayerByType(type)));

  selectMapLayerByID$ = (layerID: number) =>
    this.mapLayers$.pipe(
      map((layers) => {
        return layers.find((l) => l.id == layerID);
      })
    );

  layerForms$ = (layerName: string) =>
    this.store.pipe(select(MapLayersSelectors.selectLayerForms(layerName)));

  selectFormsByLayerID$ = (layerID: number) =>
    this.mapLayers$.pipe(
      map((layers) => layers.find((l) => l.id === layerID)),
      filter((l): l is MapLayer => !!l),
      switchMap((l: MapLayer) =>
        this.layerForms$(l.name).pipe(
          tap((forms) => {
            if (!forms) {
              this.getLayerForms(l.name);
            }
          })
        )
      )
    );

  constructor(private store: Store<fromProjects.State>) {}

  dispatch(action: Action) {
    this.store.dispatch(action);
  }

  getLayerForms(layerName: string) {
    this.store.dispatch(MapLayersActions.getLayerForms({ layerName }));
  }

  updateMapLayerGeoJSONStr(layerID: number, geoJSONStr: string) {
    this.store.dispatch(
      MapLayersActions.updateMapLayerGeoJSONStr({
        layerID,
        geoJSONStr,
      })
    );
  }
}
